import { useEffect, useMemo, useState } from 'react';
import {
  NUMBER_OF_COLUMNS_IN_FILMSTRIP,
  NUMBER_OF_ROWS_IN_FILMSTRIP,
  TILE_HEIGHT_IN_PX,
  TILE_WIDTH_IN_PX
} from '../Pages/Clip/ClipPlayer/ClipTimeline/ClipTimelineConstants';
import { roundToNearestLowerHalf } from '../Pages/Clip/ClipPlayer/ClipTimeline/ClipTimelineUtils';
import { Clip } from '@/player-config/src/types/clip-types';
import { isUploadedVideo } from '@/player-config/src/utils/mediaContentUtils';
import { getEnvConfig } from '@/constants';

/**
 * Hook to get a cropped thumbnail from filmstrip for uploaded videos
 * @param clip - The clip object
 * @param timeInSeconds - Specific time to get thumbnail for (required)
 * @returns croppedImageUrl and isUpload flag
 */
export function useCroppedThumbnail({
  clip,
  startTimeInSeconds,
  showSceneThumbnail
}: {
  clip: Clip;
  startTimeInSeconds: number;
  showSceneThumbnail: boolean;
}) {
  const [croppedImageUrl, setCroppedImageUrl] = useState<string | undefined>(undefined);

  const isUpload = useMemo(() => {
    return isUploadedVideo(clip.content.media_source_type);
  }, [clip.content.media_source_type]);

  const timeToUse = useMemo(() => {
    return roundToNearestLowerHalf(startTimeInSeconds);
  }, [startTimeInSeconds]);

  useEffect(() => {
    if (!showSceneThumbnail || !isUpload || startTimeInSeconds < 0) {
      return;
    }

    getFilmstripImageForTime(clip.content.project.id, clip.content.id, timeToUse)
      .then(async imageUrl => {
        if (imageUrl) {
          const relativeValue = timeToUse % 15;
          const rowNumber = Math.floor(relativeValue / 2.5);
          const colNumber = Math.floor((relativeValue - rowNumber * 2.5) / 0.5);

          try {
            const croppedUrl = await cropImageFromFilmstrip(imageUrl, rowNumber, colNumber);
            setCroppedImageUrl(croppedUrl);
          } catch (error) {
            console.error('Error cropping filmstrip image', error);
          }
        }
      })
      .catch(error => {
        console.error('Error fetching filmstrip image for clip thumbnail', error);
      });
  }, [isUpload, clip.content.project.id, clip.content.id, timeToUse]);

  // Cleanup blob URL when it changes or on unmount
  useEffect(() => {
    return () => {
      if (croppedImageUrl) {
        URL.revokeObjectURL(croppedImageUrl);
      }
    };
  }, [croppedImageUrl]);

  return {
    croppedImageUrl,
    isUpload
  };
}

async function cropImageFromFilmstrip(imageUrl: string, rowNumber: number, colNumber: number): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';

    img.onload = () => {
      const tileWidth = img.width / NUMBER_OF_COLUMNS_IN_FILMSTRIP;
      const tileHeight = img.height / NUMBER_OF_ROWS_IN_FILMSTRIP;

      const canvas = document.createElement('canvas');
      canvas.width = TILE_WIDTH_IN_PX;
      canvas.height = TILE_HEIGHT_IN_PX;

      const ctx = canvas.getContext('2d');
      if (!ctx) {
        reject(new Error('Could not get canvas context'));
        return;
      }

      // Crop the specific frame from the filmstrip
      ctx.drawImage(
        img,
        colNumber * tileWidth, // source X
        rowNumber * tileHeight, // source Y
        tileWidth, // source width
        tileHeight, // source height
        0, // destination X
        0, // destination Y
        TILE_WIDTH_IN_PX, // destination width
        TILE_HEIGHT_IN_PX // destination height
      );

      // Convert to blob URL
      canvas.toBlob(
        blob => {
          if (blob) {
            resolve(URL.createObjectURL(blob));
          } else {
            reject(new Error('Could not create blob'));
          }
        },
        'image/jpeg',
        1
      );
    };

    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = imageUrl;
  });
}

async function getFilmstripImageForTime(
  projectId: string,
  contentId: string,
  timeInSeconds: number
): Promise<string | null> {
  try {
    const response = await fetch(
      `${getEnvConfig('STATIC_ASSETS_CDN')}/content-lab/filmstrip/${projectId}/${contentId}/filmstrip_index.json`
    );
    const data = await response.json();

    if (!data?.filmstrip_file_names?.length) {
      return null;
    }

    const filmstripInterval = 15;
    const filmstripIndex = Math.floor(timeInSeconds / filmstripInterval);

    if (filmstripIndex >= 0 && filmstripIndex < data.filmstrip_file_names.length) {
      return `${getEnvConfig('STATIC_ASSETS_CDN')}/${data.filmstrip_file_names[filmstripIndex]}`;
    }

    return null;
  } catch (error) {
    console.error('Error fetching filmstrip image for time', error);
    return null;
  }
}
